import React, { useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";

function Connection({ onRecalculate }) {
  const { control, watch, handleSubmit, setValue } = useFormContext();
  const { t } = useTranslation();
  const [isRecalculating, setIsRecalculating] = useState(false);

  const returnAirEnabled = watch("connectionReturnAirEnabled") || false;

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    try {
      await handleSubmit(async (data) => {
        if (onRecalculate) {
          await onRecalculate(data);
        }
      })();
    } finally {
      setIsRecalculating(false);
    }
  };

  const requiredRule = {
    required: t("projectinformation.err"),
  };

  const renderConnectionSection = (label, prefix, isRequired, showMixingRatio = false) => {
    const widthName = prefix + "Width";
    const heightName = prefix + "Height";
    const airVelocityName = prefix + "AirVelocity";
    const damperName = prefix + "Damper";
    const flexibleName = prefix + "Flexible";
    const mixingRatioName = prefix + "MixingRatio";
    const widthMaxName = prefix + "WidthMax";

    const damperValue = watch(damperName);
    const flexibleValue = watch(flexibleName);
    const widthMax = watch(widthMaxName);
    const hasDamper = damperValue === true;

    const rules = isRequired ? requiredRule : {};

    return (
      <Box sx={{ mt: 2 }}>
        <Typography component="h2" variant="h5">
          {label}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            mt: 1,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Controller
            name={widthName}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => {
              const rawVal = Number(field.value);
              const isInvalidWidth =
                field.value !== "" &&
                field.value !== null &&
                field.value !== undefined &&
                (rawVal < 1000 ||
                  (widthMax !== undefined &&
                    widthMax !== "" &&
                    rawVal > Number(widthMax)));
              return (
                <Box sx={{ flex: 1 }}>
                  <TextField
                    {...field}
                    fullWidth
                    id={widthName}
                    label={t("connection.width")}
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    error={!!error || isInvalidWidth}
                    helperText={error?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">mm</InputAdornment>
                      ),
                      onWheel: (e) => e.target.blur(),
                    }}
                    inputProps={{
                      step: 1,
                      min: 1000,
                      ...(widthMax !== undefined && widthMax !== ""
                        ? { max: Number(widthMax) }
                        : {}),
                    }}
                  />
                  {isInvalidWidth && (
                    <Alert severity="warning" sx={{ mt: 0.5, py: 0, fontSize: "0.8rem" }}>
                      {t("connection.widthHelperText", { max: widthMax })}
                    </Alert>
                  )}
                </Box>
              );
            }}
          />
          <Controller
            name={heightName}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => {
              const rawVal = Number(field.value);
              const isInvalidStep =
                hasDamper &&
                field.value !== "" &&
                field.value !== null &&
                field.value !== undefined &&
                (rawVal < 210 ||
                  rawVal > 1210 ||
                  (rawVal - 210) % 100 !== 0);
              return (
                <Box sx={{ flex: 1 }}>
                  <TextField
                    {...field}
                    fullWidth
                    id={heightName}
                    label={t("connection.height")}
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    error={!!error || isInvalidStep}
                    helperText={error?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">mm</InputAdornment>
                      ),
                      onWheel: (e) => e.target.blur(),
                    }}
                    inputProps={{
                      step: hasDamper ? 100 : 1,
                      ...(hasDamper ? { min: 210, max: 1210 } : {}),
                    }}
                  />
                  {hasDamper && isInvalidStep && (
                    <Alert severity="warning" sx={{ mt: 0.5, py: 0, fontSize: "0.8rem" }}>
                      {t("connection.heightHelperText")}
                    </Alert>
                  )}
                </Box>
              );
            }}
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <Controller
            name={airVelocityName}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                disabled
                id={airVelocityName}
                label={t("connection.airVelocity")}
                type="number"
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error?.message}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="start">m/s</InputAdornment>
                  ),
                }}
                inputProps={{
                  step: 0.01,
                }}
              />
            )}
          />
        </Box>
        {showMixingRatio && (
          <Box sx={{ mt: 1 }}>
            <Controller
              name={mixingRatioName}
              control={control}
              defaultValue={0}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  id={mixingRatioName}
                  label={t("connection.mixingRatio")}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                  inputProps={{
                    step: 1,
                    min: 0,
                    max: 100,
                  }}
                />
              )}
            />
          </Box>
        )}
        <Box sx={{ display: "flex", gap: "16px", mt: 1 }}>
          <Controller
            name={damperName}
            control={control}
            defaultValue={true}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value ?? true}
                    onChange={(e) => {

                      onChange(e.target.checked);
                    }}
                  />
                }
                label={t("connection.damper")}
              />
            )}
          />
          <Controller
            name={flexibleName}
            control={control}
            defaultValue={false}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value || false}
                    onChange={(e) => {

                      onChange(e.target.checked);
                    }}
                  />
                }
                label={t("connection.flexibleConnection")}
              />
            )}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Container component="main" sx={{ mb: 4, "min-width": "100%" }}>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: "15px 24px 24px 24px" }, align: "center" }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <Typography sx={{ textAlign: "center", fontSize: "38px", mb: "0px" }}>
            {t("connection.title")}
          </Typography>
        </Box>
        <Box
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: "16px" }}
        >
          {/* Recalculate button */}
          <Box>
            <Button
              variant="contained"
              onClick={handleRecalculate}
              disabled={isRecalculating}
            >
              {isRecalculating
                ? "Recalculating..."
                : t("connection.recalculate")}
            </Button>
          </Box>

          <Divider sx={{ m: 1 }} />

          {/* Fresh Air */}
          {renderConnectionSection(
            t("connection.freshAir"),
            "connectionFreshAir",
            true,
          )}

          <Divider sx={{ m: 1 }} />

          {/* Supply Air */}
          {renderConnectionSection(
            t("connection.supplyAir"),
            "connectionSupplyAir",
            true,
          )}

          <Divider sx={{ m: 1 }} />

          {/* Return Air toggle */}
          <Controller
            name="connectionReturnAirEnabled"
            control={control}
            defaultValue={false}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value || false}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                }
                label={t("connection.enableReturnAir")}
              />
            )}
          />

          {/* Return Air - only shown when enabled */}
          {returnAirEnabled &&
            renderConnectionSection(
              t("connection.returnAir"),
              "connectionReturnAir",
              true,
              true,
            )}
        </Box>
      </Paper>
    </Container>
  );
}

export default Connection;
